import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ReportAnalysisPrint - A clean print-only route that renders the report
 * without any SaaS chrome (sidebar, header, toolbar, etc.)
 * 
 * This route is used by Playwright to generate PDFs that match the Preview exactly.
 * 
 * URL: /credit-report/analysis/print?report_id=xxx&token=xxx
 */
const ReportAnalysisPrint = () => {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('report_id');
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);

  // Inject print-specific CSS for PDF export
  const injectPrintStyles = (html) => {
    const printStyles = `
      <style>
        /* Print-specific CSS for PDF export */
        @page {
          size: Letter;
          margin: 0;
        }
        
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        /* Each page is a fixed Letter-size container */
        .pdf-page {
          width: 8.5in !important;
          height: 11in !important;
          min-height: 11in !important;
          max-height: 11in !important;
          margin: 0 !important;
          padding: 0.55in !important;
          box-sizing: border-box !important;
          background: #ffffff !important;
          page-break-after: always !important;
          break-after: page !important;
          overflow: hidden !important;
          position: relative !important;
        }
        
        .pdf-page:last-of-type {
          page-break-after: auto !important;
          break-after: auto !important;
        }
        
        /* Cover page - no padding, full bleed */
        .cover-page {
          width: 8.5in !important;
          height: 11in !important;
          min-height: 11in !important;
          max-height: 11in !important;
          margin: 0 !important;
          padding: 0 !important;
          page-break-after: always !important;
          break-after: page !important;
          overflow: hidden !important;
        }
        
        /* Final page - no padding, full bleed */
        .final-page {
          width: 8.5in !important;
          height: 11in !important;
          min-height: 11in !important;
          max-height: 11in !important;
          margin: 0 !important;
          padding: 0 !important;
          page-break-after: auto !important;
          break-after: auto !important;
          overflow: hidden !important;
        }
        
        /* Prevent cards from stretching */
        .summary-card,
        .profile-card,
        .factor-card,
        .factor-card-premium,
        .provider-card,
        .tool-card,
        .checklist-card,
        .loan-card,
        .insight-box,
        .highlight-card,
        .access-card,
        .step-card,
        .habit-card {
          height: auto !important;
          min-height: auto !important;
          align-self: start !important;
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        
        /* Prevent grid items from stretching */
        .summary-grid,
        .profile-cards,
        .factors-grid-premium,
        .provider-cards-grid,
        .habits-grid,
        .credit-access-grid {
          align-items: start !important;
        }
        
        /* Ensure proper content placement */
        .page-footer {
          position: absolute !important;
          left: 0.55in !important;
          right: 0.55in !important;
          bottom: 0.28in !important;
        }
        
        /* Keep sections together where possible */
        .section-block,
        .loan-section,
        .documentation-checklist,
        .docs-checklist,
        .interest-difference-callout {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }
        
        /* Tables - prevent overflow */
        .data-table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        
        /* Hide any preview-specific elements */
        .preview-toolbar,
        .zoom-controls,
        .preview-nav {
          display: none !important;
        }
      </style>
    `;
    
    // Insert styles before closing head tag
    let modifiedHtml = html.replace('</head>', `${printStyles}</head>`);
    
    // Rewrite relative image paths to absolute URLs
    const assetsBaseUrl = `${API_URL}/api/report-assets`;
    modifiedHtml = modifiedHtml.replace(/src="bureaus\//g, `src="${assetsBaseUrl}/bureaus/`);
    modifiedHtml = modifiedHtml.replace(/src="illustrations\//g, `src="${assetsBaseUrl}/illustrations/`);
    modifiedHtml = modifiedHtml.replace(/src="providers\//g, `src="${assetsBaseUrl}/providers/`);
    modifiedHtml = modifiedHtml.replace(/src="provider-logos\//g, `src="${assetsBaseUrl}/provider-logos/`);
    
    return modifiedHtml;
  };

  // Fetch the HTML content
  const fetchHtml = useCallback(async () => {
    if (!reportId || !token) {
      setError('Missing report_id or token');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/report-analysis/${reportId}/pdf-html`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch report HTML');
      }
      
      const html = await response.text();
      const printHtml = injectPrintStyles(html);
      setHtmlContent(printHtml);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [reportId, token]);

  useEffect(() => {
    fetchHtml();
  }, [fetchHtml]);

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#ffffff'
      }}>
        <div>Loading report...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#ffffff',
        color: '#ef4444'
      }}>
        <div>Error: {error}</div>
      </div>
    );
  }

  // Render the HTML content directly
  if (htmlContent) {
    return (
      <div 
        id="print-root"
        dangerouslySetInnerHTML={{ __html: htmlContent }} 
        style={{ margin: 0, padding: 0 }}
      />
    );
  }

  return null;
};

export default ReportAnalysisPrint;
